import { useContext, useState, useEffect, useMemo } from "react";
import { MyContext } from "../../App";
import { useDispatch, useSelector } from "react-redux";
import { addItemToCart } from "../../store/slices/CartSlice";

const ProductModal = ({ isOpen, onClose, product }) => {
  const [activePriceIndex, setActivePriceIndex] = useState(0);
  const [addingId, setAddingId] = useState(null);
  const context = useContext(MyContext);
  const dispatch = useDispatch();
  const { cartItems, loading: cartLoading } = useSelector(
    (state) => state.cart
  );

  const cartPriceIds = useMemo(() => {
    const ids = new Set();
    cartItems.forEach((item) => {
      if (item.productId?._id === product._id) {
        ids.add(item.priceId);
      }
    });
    return ids;
  }, [cartItems, product._id]);

  const handlePriceClick = (index) => {
    setActivePriceIndex(index);
  };

  const handleAddToCart = async (selectedPrice) => {
    if (selectedPrice) {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Please login to add to cart.",
        });

        return;
      }
      setAddingId(selectedPrice._id);

      const data = {
        priceId: selectedPrice._id,
        productId: product._id,
        quantity: 1,
      };

      try {
        await dispatch(addItemToCart(data)).unwrap();
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Product added to cart successfully!",
        });
      } catch (error) {
        context.setAlertBox({
          open: true,
          error: true,
          msg: error || "Failed to add product to cart.",
        });
      } finally {
        setAddingId(null);
        onClose();
      }
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return isOpen ? (
    <div
      className="fixed inset-0 z-[455657568776] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white p-6 rounded-lg w-11/12 max-w-2xl overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">{product.name}</h2>
            <p className="text-sm text-gray-500">{product.brand}</p>
            <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
              Limited Time Deal ✨
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="flex overflow-x-auto mb-6">
          {product.images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={product.name}
              className="w-24 h-24 rounded-md mr-4"
            />
          ))}
        </div>

        <h3 className="text-lg font-semibold mb-2">Choose a Size</h3>

        <div className="h-48 overflow-y-auto">
          {product.prices.map((price, index) => {
            const isInCart = cartPriceIds.has(price._id);
            const isAdding = addingId === price._id;
            return (
              <div
                key={price._id}
                className={`flex items-center justify-between py-2 border-b border-gray-200 ${
                  activePriceIndex === index ? "bg-gray-100" : ""
                }`}
                onClick={() => handlePriceClick(index)}
              >
                <div>
                  <h4 className="text-md font-medium">
                    {price.quantity} &nbsp;{price.type}
                  </h4>
                  <p>InStock&nbsp;:&nbsp;{price.countInStock}</p>
                  <div>
                    <span className="text-sm text-gray-500 line-through">
                      ₹{price.oldPrice}
                    </span>
                    <span className="text-sm text-green-600 ml-2">
                      ₹{price.actualPrice}
                    </span>
                    <span className="text-white bg-orange-500 rounded-md text-xs font-normal px-1.5 py-1 block md:inline md:ml-2.5">
                      {price.discount}% OFF
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Save ₹{price.oldPrice - price.actualPrice}
                  </p>
                </div>
                <button
                  className={`px-4 py-2 bg-orange-500 text-black text-sm rounded hover:bg-orange-600 transition-all duration-300 ease-in-out ${
                    isAdding || isInCart || price.countInStock < 1
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={() => handleAddToCart(price)}
                  disabled={isAdding || isInCart || price.countInStock < 1}
                >
                  {price.countInStock < 1
                    ? "Out of Stock"
                    : isAdding
                    ? "Adding..."
                    : isInCart
                    ? "Added to Cart"
                    : "Add to Cart"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;
};

export default ProductModal;
